import { forwardRef } from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import Close from '@/icons/close.svg';
import { cn } from '@/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = forwardRef(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn(
			'fixed inset-0 z-[49] bg-black/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
			className
		)}
		{...props}
	/>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

function DialogCloseButton() {
	return (
		<DialogPrimitive.Close className="absolute right-4 top-2 flex size-8 items-center justify-center rounded-sm transition-colors hover:bg-grey-100 active:bg-grey-200 disabled:pointer-events-none">
			<Close className="h-5 w-5 text-black" />
			<span className="sr-only">Close</span>
		</DialogPrimitive.Close>
	);
}

const DialogContent = forwardRef(
	({ className, children, hasCross = true, forceMount, ...props }, ref) => {
		return (
			<DialogPortal>
				<DialogOverlay />
				<DialogPrimitive.Content
					ref={ref}
					className={cn(
						'fixed left-[50%] top-[50%] z-50 grid w-full translate-x-[-50%] translate-y-[-50%] rounded-lg bg-background shadow-lg outline-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
						className
					)}
					{...props}
				>
					{children}
					{hasCross && <DialogCloseButton />}
				</DialogPrimitive.Content>
			</DialogPortal>
		);
	}
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

function DialogHeader({ className, ...props }) {
	return (
		<div
			className={cn(
				'flex flex-col space-y-1.5 text-center sm:text-left',
				className
			)}
			{...props}
		/>
	);
}
DialogHeader.displayName = 'DialogHeader';

function DialogFooter({
	className,
	singleButton,
	mobileSingleColumn = false,
	...props
}) {
	return (
		<div
			className={cn(
				'grid grid-cols-2 gap-2 border-t border-grey-200 p-4 pb-6',
				singleButton && 'grid-cols-1',
				mobileSingleColumn && 'flex flex-col-reverse md:grid',
				className
			)}
			{...props}
		/>
	);
}
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = forwardRef(({ className, ...props }, ref) => (
	<DialogPrimitive.Title
		ref={ref}
		className={cn(
			'text-lg font-semibold leading-none tracking-tight',
			className
		)}
		{...props}
	/>
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = DialogPrimitive.Description;

const DialogTitleUnstyled = forwardRef(({ children, ...props }, ref) => (
	<DialogPrimitive.Title ref={ref} asChild>
		<h3 {...props}>{children}</h3>
	</DialogPrimitive.Title>
));

DialogTitleUnstyled.displayName = DialogPrimitive.Title.displayName;

function DialogTop({ title, description, className, icon }) {
	return (
		<DialogHeader className="flex h-12 flex-row items-center justify-between border-b border-grey-200 px-4">
			<div className="flex items-center gap-1">
				{icon}
				<DialogTitle
					className={cn(
						'font-title text-xs font-semibold leading-5 md:text-sm md:leading-6',
						className
					)}
				>
					{title}
				</DialogTitle>
			</div>
			<VisuallyHidden asChild>
				<DialogDescription>{description || title}</DialogDescription>
			</VisuallyHidden>
		</DialogHeader>
	);
}

export {
	Dialog,
	DialogPortal,
	DialogOverlay,
	DialogClose,
	DialogTrigger,
	DialogContent,
	DialogHeader,
	DialogFooter,
	DialogTitle,
	DialogDescription,
	DialogTitleUnstyled,
	DialogTop,
	DialogCloseButton
};

export default Dialog;
